import { Box, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useQuery } from 'react-query';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import {
  KEY_CUMULATIVE,
  KEY_MEDAL,
  KEY_READ_TEXT,
  KEY_SCHOOL_MONTHLY_READ_MEDAL_LIST,
  KEY_SCHOOL_MONTHLY_READ_STAT_LIST,
  KEY_SCHOOL_MONTHLY_WALK_MEDAL_LIST,
  KEY_SCHOOL_MONTHLY_WALK_STAT_LIST,
  KEY_SCHOOL_MONTHLY_WRITE_MEDAL_LIST,
  KEY_SCHOOL_MONTHLY_WRITE_STAT_LIST,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';

import { getClassMonthlyReadRankList } from '../../common/api/ApiClassStat';
import {
  downloadSchoolMonthlyDataList,
  downloadSchoolMonthlyMedalDataList,
  getSchoolMonthlyReadMedalList,
  getSchoolMonthlyReadStatList,
  getSchoolMonthlyWalkMedalList,
  getSchoolMonthlyWalkStatList,
  getSchoolMonthlyWriteMedalList,
  getSchoolMonthlyWriteStatList,
} from '../../common/api/ApiSchool';
import { selectSchoolInfoState } from '../../common/atom';
import { RwwGraphByMedal } from './RwwGraphByMedal';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwMedalStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
}) => {
  const schoolClassInfo = useRecoilValue(selectSchoolInfoState);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchSchoolMonthlyReadMedal,
  } = useQuery(
    KEY_SCHOOL_MONTHLY_READ_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolMonthlyReadMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolMonthlyWalkMedal } = useQuery(
    KEY_SCHOOL_MONTHLY_WALK_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolMonthlyWalkMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchSchoolMonthlyWriteMedal } = useQuery(
    KEY_SCHOOL_MONTHLY_WRITE_MEDAL_LIST,
    () => {
      if (reqRequest) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          year: getYear(startDate * 1000),
          month: getMonthText(startDate * 1000),
        };
        return getSchoolMonthlyWriteMedalList(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadSchoolMonthlyData = () => {
    const newData: any = {
      type: getTypeWord(statType),
      dataType: KEY_MEDAL,
      schoolSeq: schoolClassInfo.schoolSeq,
      year: getYear(startDate * 1000),
      month: getMonthText(startDate * 1000),
    };
    downloadSchoolMonthlyMedalDataList(newData);
  };

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchSchoolMonthlyReadMedal();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0 && reqRequest) {
      refetchSchoolMonthlyReadMedal();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchSchoolMonthlyReadMedal();
    }
  }, []);

  return (
    <>
      <Paper>
        <SwTitleDownloadMore title='우리 학교 읽기/걷기/쓰기 메달 데이터' />
        <SwRwwTab
          onChangeTab={value => {
            setStatType(
              value === 0
                ? KEY_READ_TEXT
                : value === 1
                ? KEY_WALK_TEXT
                : KEY_WRITE_TEXT
            );
            value === 0
              ? refetchSchoolMonthlyReadMedal()
              : value === 1
              ? refetchSchoolMonthlyWalkMedal()
              : refetchSchoolMonthlyWriteMedal();
          }}
          onDownload={() => downloadSchoolMonthlyData()}
        />
        <RwwGraphByMedal
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.rate}
          isPdf={false}
        />
      </Paper>
    </>
  );
};
